import React from "react";

import { useState, useEffect, useMemo } from "react";
import { Platform, QualityAlert } from "../../models/quality-alert";
import { Location as TQSLocation } from "../../models/location";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCar,
  faLocationPin,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import QualityAlertBoxes from "./quality-alert-boxes";
import { faCalendar, faCircleDot } from "@fortawesome/free-regular-svg-icons";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch, RootState } from "../../reducers/store";
import { fetchExtendedQualityAlert } from "../../reducers/qualityAlertSlice";
import { useAppDispatch } from "../../reducers/store";
import { getSupplierDataById as getSupplierDataByIdAction, getSuggestedLocations as getSuggestedLocationsAction } from "../../reducers/supplierSlice";

interface QualityAlertDetailsProps {
  issue: QualityAlert;
  allLocations: TQSLocation[];
  index: number;
  expandedRow: number;
  updateQualityAlert: (qa: QualityAlert) => void;
}

export default function QualityAlertDetails({
  issue,
  allLocations,
  index,
  expandedRow,
  updateQualityAlert,
}: QualityAlertDetailsProps): JSX.Element {
  const dispatch = useAppDispatch();
  const [tabClicked, setTabClicked] = useState<string>("IssueDetails");
  const [searchQuery, setSearchQuery] = useState("");

  const [suggestedLocations, setSuggestedLocations] = useState<number[]>([]);

  const [actions, setActions] = useState<any>([]);

  const [loading, setLoading] = useState(false);

  // Only fetch when this specific row is expanded
  const extendedQA = useSelector(
    (state: RootState) => state.qa.extendedModels[issue?.id],
  );

  // Get location by ID
  const getLocationById = (locationId: number): TQSLocation | undefined => {
    return allLocations.find((location) => location.id === locationId);
  };

  // Get the issuing location from qualityAlertLocationInfo
  const issuingLocation = useMemo(() => {
    const issuingLocationInfo = issue.qualityAlertLocationInfo.find((info) => {
      return (
        info.status === "Issuing Location" ||
        (info.location as unknown as any).issueLocationId === issue.location
      );
    });

    if (issuingLocationInfo) {
      return issuingLocationInfo.locationId;
    }

    // Fallback to the main location if no issuing location found
    const fallbackLocation =
      typeof issue.location === "number"
        ? getLocationById(issue.location)
        : issue.location;

    return fallbackLocation;
  }, [issue.qualityAlertLocationInfo, issue.location, allLocations]);

  // Extract and transform the data for actions taken
  useEffect(() => {
    // Use extended data if available, otherwise fall back to regular issue data
    const locationInfo =
      extendedQA?.qualityAlertLocationInfo || issue.qualityAlertLocationInfo;

    const transformedData = locationInfo.flatMap((material) =>
      (material.actionsTaken || []).map((action) => ({
        ...action,
        location:
          typeof material.location === "number"
            ? getLocationById(material.location)
            : material.location,
        actionTakenTime: new Date(action.createTime).getTime(),
        status: material.status,
      })),
    );

    // Step 2: Sort the transformed data by actionTakenTime
    const sortedData = transformedData.sort(
      (a, b) => a.actionTakenTime - b.actionTakenTime,
    );

    // Step 3: Set the state with the sorted data
    setActions(sortedData);
  }, [issue, allLocations, extendedQA]);

  const filteredActions = actions.filter((action: any) => {
    const searchText = searchQuery.toLowerCase();
    const normalizedStatus =
      action.status === "Checking For Issue"
        ? "investigating"
        : action.status.toLowerCase();

    return (
      normalizedStatus.includes(searchText) ||
      action.createUser.firstName.toLowerCase().includes(searchText) ||
      action.createUser.lastName.toLowerCase().includes(searchText) ||
      action.desc.toLowerCase().includes(searchText) ||
      action.location.oem.name.toLowerCase().includes(searchText) ||
      action.location.name.toLowerCase().includes(searchText)
    );
  });

  const DefaultProfile =
    "https://s3.amazonaws.com/com.teamqualityservices.qnet/images/Logo+Q+Only.png";

  //On load execute fucntion to get suggested locations

  const loadData = async (controller: AbortController) => {
    try {
      // Only fetch if this is the expanded row
      if (expandedRow === index) {
        // Check if we already have the extended data
        const hasExtendedData = Boolean(
          extendedQA?.qualityAlertLocationInfo?.some(
            (info) => info.actionsTaken?.length,
          ),
        );

        setLoading(true);

        if (!hasExtendedData) {
          await Promise.all([
            getSuggestedLocations(controller.signal),
            dispatch(fetchExtendedQualityAlert(issue.id))
          ]);
        } else {
          await getSuggestedLocations(controller.signal);
        }
      }
    } catch (error: unknown) {
      if (error instanceof Error) {
        if (error.name === "AbortError") {
          console.log("Fetch aborted");
        } else {
          console.error("Error in loadData:", error);
        }
      }
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    // Only run effect if this is the expanded row
    if (index === expandedRow && expandedRow !== null && issue?.id) {
      const controller = new AbortController();
      loadData(controller);
      return () => {
        controller.abort();
        setLoading(false);
      };
    }
  }, [expandedRow, index, issue?.id]);

  const getSuggestedLocations = async (signal: AbortSignal) => {
    try {
      // Fetch supplier data and pass signal for cancellation
      const supplierResult = await dispatch(getSupplierDataByIdAction({
        supplierId: issue.supplierId, 
        signal
      }));

      if (!supplierResult.payload) {
        throw new Error('Failed to fetch supplier data');
      }

      const supplier = supplierResult.payload as { penetrationMatrixId?: number };
      const PMId = supplier.penetrationMatrixId;
      
      if (PMId) {
        // Fetch suggested locations and pass signal for cancellation
        const locationsResult = await dispatch(getSuggestedLocationsAction({
          supplierId: issue.supplierId, 
          signal
        }));

        if (locationsResult.payload) {
          setSuggestedLocations(locationsResult.payload as number[]); // Set suggested locations state
        }
      }
    } catch (error) {
      if ((error as Error).name === "AbortError") {
        console.log("Fetch request was canceled");
      } else {
        console.error("Error fetching locations:", error);
      }
    }
  };

  // Date Formatting
  const formatDateTime = (input: number | Date | string) => {
    let date: Date;

    if (typeof input === "number" || typeof input === "string") {
      date = new Date(input);
    } else if (input instanceof Date) {
      date = input;
    } else {
      throw new Error(
        "Invalid input type. Expected a number, a Date object, or a string.",
      );
    }

    const dateOptions: Intl.DateTimeFormatOptions = {
      day: "2-digit",
      month: "short",
      year: "numeric",
      timeZone: "America/New_York",
    };

    const timeOptions: Intl.DateTimeFormatOptions = {
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "America/New_York",
      hour12: true,
    };

    const formattedDate = new Intl.DateTimeFormat("en-US", dateOptions).format(
      date,
    );
    const formattedTime = new Intl.DateTimeFormat("en-US", timeOptions).format(
      date,
    );

    return `${formattedDate} ${formattedTime} EDT`;
  };

  // Add this function to handle tab clicks
  const showDetails = (tab: string) => {
    setTabClicked(tab);
  };

  return (
    <>
      <div className="flex h-full flex-1">
        {loading ? (
          <div className="flex h-full w-full items-center justify-center">
            <span className="loading loading-spinner h-[40px] w-[40px] text-tqsYellow"></span>
          </div>
        ) : (
          <>
            <div className="flex h-full w-1/2 flex-col rounded-md border border-gray-300 bg-white p-4">
              <p className="text-2xl font-semibold text-[#212322]">
                {extendedQA?.level === 1
                  ? `QA ${extendedQA?.number}`
                  : `NM ${extendedQA?.number}`}
              </p>
              <p className="text-lg font-semibold text-gray-400">
                {`PRR ${extendedQA?.prrNumber ? `${extendedQA.prrNumber !== "Unknown" ? "#" : ""}${extendedQA.prrNumber}` : "Unknown"}`}
              </p>

              <div className="mt-3 flex flex-row items-center">
                <div className="h-100 flex flex-col">
                  <div className="mb-3 flex flex-row items-center">
                    <FontAwesomeIcon
                      className="mr-[8px] h-3 w-3 text-gray-400"
                      icon={faCircleDot}
                    />

                    <p className="text-[12px] text-gray-400">Status</p>
                  </div>
                  <div className="mb-3 flex flex-row items-center">
                    <FontAwesomeIcon
                      className="mr-[8px] h-3 w-3 text-gray-400"
                      icon={faCalendar}
                    />

                    <p className="text-[12px] text-gray-400">Created Time</p>
                  </div>
                  <div className="mb-3 flex flex-row items-center">
                    <FontAwesomeIcon
                      className="mr-[8px] h-3 w-3 text-gray-400"
                      icon={faLocationPin}
                    />

                    <p className="text-[12px] text-gray-400">
                      Issuing Location
                    </p>
                  </div>
                  <div className="mb-3 flex flex-row items-center">
                    <FontAwesomeIcon
                      icon={faCar}
                      className="mr-[8px] h-3 w-3 text-gray-400"
                    />

                    <p className="text-[12px] text-gray-400">Platform</p>
                  </div>
                </div>
                {/*  */}

                <div className="h-100 ml-3 flex flex-col items-start">
                  <p
                    className={`mb-1 inline-block rounded-md px-[16px] py-[4px] text-[12px] ${
                      extendedQA?.status === "Open"
                        ? "bg-tqsLightRed font-bold text-tqsRed"
                        : "bg-tqsLightGreen font-bold text-tqsGreen"
                    }`}
                  >
                    {extendedQA?.status}
                  </p>
                  <p className="mb-3 text-[12px] text-[#212322]">
                    {formatDateTime(extendedQA?.createTime?.toString() ?? new Date().toISOString())}
                  </p>
                  <p className="mb-3 text-[12px] text-[#212322]">
                    {(issuingLocation as TQSLocation)?.name ?? 'Unknown Location'}
                  </p>
                  <p className="mb-3 text-[12px] text-[#212322]">
                    {(extendedQA?.platform as Platform)?.desc ?? 'Unknown Platform'}
                  </p>
                </div>
              </div>
              <div className="flex flex-row">
                <button
                  className={`m-2 border-b-4 p-2 ${
                    tabClicked === "IssueDetails"
                      ? "border-tqsYellow"
                      : "border-transparent"
                  }`}
                  onClick={() => showDetails("IssueDetails")}
                >
                  Issue Details
                </button>
                <button
                  className={`m-2 border-b-4 p-2 ${
                    tabClicked === "ActionsTaken"
                      ? "border-tqsYellow"
                      : "border-transparent"
                  }`}
                  onClick={() => showDetails("ActionsTaken")}
                >
                  Actions Taken
                </button>
              </div>
              <div className="relative mb-7">
                <div className="absolute left-0 right-0 m-3 -mx-4 mb-2 border-t border-gray-300"></div>
              </div>
              {tabClicked === "IssueDetails" && (
                <div className="flex flex-col">
                  <div className="mb-2 flex flex-row items-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="mr-2 size-5 text-gray-600"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M21.75 6.75a4.5 4.5 0 0 1-4.884 4.484c-1.076-.091-2.264.071-2.95.904l-7.152 8.684a2.548 2.548 0 1 1-3.586-3.586l8.684-7.152c.833-.686.995-1.874.904-2.95a4.5 4.5 0 0 1 6.336-4.486l-3.276 3.276a3.004 3.004 0 0 0 2.25 2.25l3.276-3.276c.256.565.398 1.192.398 1.852Z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M4.867 19.125h.008v.008h-.008v-.008Z"
                      />
                    </svg>

                    <p className="text-sm text-gray-600"> Part Information</p>
                  </div>
                  <div className="flex h-full w-full flex-col rounded-xl bg-[#f6f6f6]">
                    {extendedQA?.qualityAlertLocationInfo
                      .flatMap((q) => q.qualityAlertDiscrepantMaterial)
                      .map((material, index) => (
                        <div key={index}>
                          <div className="flex flex-row items-center justify-between">
                            <div className="flex flex-col">
                              <div className="flex flex-col items-start p-4">
                                <p className="mr-2 text-sm">
                                  {material?.part.id}
                                </p>
                                <p className="text-sm text-gray-400">
                                  Part Number
                                </p>
                              </div>
                            </div>
                            <div className="flex flex-col items-start p-4">
                              <p className="text-sm">
                                {material?.part?.category}
                              </p>
                              <p className="text-sm text-gray-400">
                                Part Category
                              </p>
                            </div>
                          </div>
                          <div className="flex flex-col p-4">
                            <div className="flex flex-row text-center">
                              <p className="pr-2 text-lg">{material?.qty}</p>
                              <div className="mb-1 rounded-md border-none bg-[#d7d7d8] px-2.5 py-1 text-sm font-semibold text-gray-500">
                                {material?.disposition || "Blank"}
                              </div>
                            </div>
                            <p className="text-sm text-gray-400">
                              Pieces of Discrepant Material
                            </p>
                          </div>
                          {index > 0 ? (
                            <div className="my-4 h-px w-full bg-gray-300"></div>
                          ) : null}
                        </div>
                      ))}
                  </div>
                  <div className="mt-4 flex flex-col">
                    <div className="flex flex-row text-center">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="mr-2 size-5"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25ZM6.75 12h.008v.008H6.75V12Zm0 3h.008v.008H6.75V15Zm0 3h.008v.008H6.75V18Z"
                        />
                      </svg>

                      <p className="mb-4 text-base text-gray-600">
                        Issue Summary
                      </p>
                    </div>
                    <div className="h-full w-full rounded-xl border border-gray-700">
                      <p className="p-4 text-sm">{extendedQA?.issueDetail}</p>
                    </div>
                  </div>
                  <div className="mt-4 flex flex-col">
                    <div className="flex flex-row text-center">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="mr-2 size-5"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="m18.375 12.739-7.693 7.693a4.5 4.5 0 0 1-6.364-6.364l10.94-10.94A3 3 0 1 1 19.5 7.372L8.552 18.32m.009-.01-.01.01m5.699-9.941-7.81 7.81a1.5 1.5 0 0 0 2.112 2.13"
                        />
                      </svg>
                      <div className="flex flex-col">
                        <p className="text-gray-600">
                          Attachments (
                          {extendedQA?.attachments ? extendedQA.attachments.length : 0})
                        </p>
                        <div className="flex flex-row">
                          {extendedQA?.attachments &&
                          extendedQA.attachments.length > 0 ? (
                            extendedQA?.attachments.map((attachment, index) => (
                              <div
                                key={index}
                                className="mt-2 mr-2 cursor-pointer hover:opacity-80 transition-opacity"
                                style={{ cursor: 'pointer' }}
                                onClick={() => window.open(attachment?.attachment?.downloadURL, '_blank')}
                                title={`Click to open ${attachment?.attachment?.filename || 'attachment'} in new tab`}
                              >
                                <img
                                  className="h-20 w-20"
                                  src={attachment?.attachment?.downloadURL}
                                  alt={`Attachment ${index + 1}`}
                                />
                              </div>
                            ))
                          ) : (
                            <p>No images</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {tabClicked === "ActionsTaken" && (
                <>
                  <div className="relative flex w-full items-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="1.5"
                      stroke="currentColor"
                      className="absolute left-2 top-1/2 h-6 w-6 -translate-y-1/2 transform text-gray-400"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"
                      />
                    </svg>
                    <input
                      className="w-full rounded-md border border-gray-300 bg-white p-2 pl-10 text-sm"
                      type="text"
                      placeholder="Search Activity"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>

                  <div className="relative" style={{ marginBottom: "42px" }}>
                    <div className="absolute left-0 right-0 m-3 -mx-4 mb-2 border-t border-gray-300"></div>
                  </div>

                  <div>
                    {filteredActions.map(
                      (action: any, index: React.Key | null | undefined) => (
                        <div className="mb-4" key={index}>
                          <div className="mx-auto flex flex-row justify-between">
                            <div className="mr-5">
                              <div className="h-full">
                                {action?.status === "Issue Found" ? (
                                  <div className="relative rounded-full bg-tqsLightRed p-2 text-tqsRed">
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      viewBox="0 0 24 24"
                                      fill="currentColor"
                                      className="h-5 w-5"
                                    >
                                      <path d="M21.731 2.269a2.625 2.625 0 0 0-3.712 0l-1.157 1.157 3.712 3.712 1.157-1.157a2.625 2.625 0 0 0 0-3.712ZM19.513 8.199l-3.712-3.712-12.15 12.15a5.25 5.25 0 0 0-1.32 2.214l-.8 2.685a.75.75 0 0 0 .933.933l2.685-.8a5.25 5.25 0 0 0 2.214-1.32L19.513 8.2Z" />
                                    </svg>
                                    <div className="absolute left-[50%] top-[-30px] h-6 w-[1px] -translate-x-1/2 transform bg-gray-300"></div>
                                  </div>
                                ) : action?.status === "Checking For Issue" ? (
                                  <div className="relative rounded-full bg-gray-200 p-2 text-gray-700">
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      viewBox="0 0 24 24"
                                      fill="currentColor"
                                      className="h-5 w-5"
                                    >
                                      <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" />
                                      <path
                                        fillRule="evenodd"
                                        d="M1.323 11.447C2.811 6.976 7.028 3.75 12.001 3.75c4.97 0 9.185 3.223 10.675 7.69.12.362.12.752 0 1.113-1.487 4.471-5.705 7.697-10.677 7.697-4.97 0-9.186-3.223-10.675-7.69a1.762 1.762 0 0 1 0-1.113ZM17.25 12a5.25 5.25 0 1 1-10.5 0 5.25 5.25 0 0 1 10.5 0Z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                    <div className="absolute left-[50%] top-[-30px] h-6 w-[1px] -translate-x-1/2 transform bg-gray-300"></div>
                                  </div>
                                ) : null}
                              </div>
                            </div>
                            <div>
                              <div className="flex flex-col">
                                <div className="flex flex-row">
                                  <p className="mr-4 text-xs font-bold">
                                    {action.location?.oem?.name ?? ""} -{" "}
                                    {action.location?.name}
                                  </p>
                                </div>
                                {action.status === "Issue Found" ? (
                                  <>
                                    <div className="flex flex-row">
                                      <p className="text-xs text-[#212322]">
                                        {action.createUser?.firstName}{" "}
                                        {action.createUser?.lastName} Created
                                        alert #{extendedQA.number}
                                      </p>
                                    </div>
                                  </>
                                ) : (
                                  <p className="text-xs text-[#212322]">
                                    {action.status === "Checking For Issue"
                                      ? "Marked the Issue Status as Investigating"
                                      : `Marked the Issue Status as ${action?.status}`}
                                  </p>
                                )}
                              </div>
                            </div>
                            <p className="ml-3 text-xs text-gray-400">
                              {formatDateTime(action?.actionTakenTime)}
                            </p>
                          </div>
                          {action?.status === "Issue Found" && (
                            <div className="mt-4 flex flex-row justify-end bg-white">
                              <img
                                className="h-8 w-8"
                                src={
                                  action?.createUser?.photoAttachment
                                    ?.downloadURL ?? "/dashboard/avatar.png"
                                }
                                onError={(e) =>
                                  (e.currentTarget.src =
                                    "/dashboard/avatar.png")
                                } // Fallback if the image fails to load
                                alt="Profile"
                              />
                              <div className="ml-2 h-full w-3/4 rounded-md bg-[#f6f6f6]">
                                <p className="p-1 pl-2 text-xs font-semibold text-[#8e8e93]">
                                  {action?.desc}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      ),
                    )}
                  </div>
                </>
              )}
            </div>
            <div className="flex flex-col">
              <QualityAlertBoxes
                qualityAlertLocationInfo={issue.qualityAlertLocationInfo}
                allLocations={allLocations}
                selectedLocationId={(issuingLocation as TQSLocation).id} // Updated this line
                issueNumber={issue.number}
                suggestedLocations={suggestedLocations}
              />
            </div>
          </>
        )}
      </div>
    </>
  );
}
